<script>
  import Vue from 'vue';
  import Raphael from 'raphael';
  import TweenLite from 'gsap/umd/TweenLite';
  import { Quad, Linear } from 'gsap/umd/EasePack';
  import { required, email } from 'vuelidate/lib/validators';
  import { validationMixin } from 'vuelidate';

  import { declOfNums } from '@/scripts/old/helpers/utils';
  import Icon from '@/scripts/old/modules/Icon.vue';
  import Popup from '@/scripts/old/modules/Popup.vue';
  import Countdown from '@/scripts/old/modules-refactored/Countdowns/Countdown.vue';
  import Unlockshare from '@/scripts/old/modules/Unlockshare';

  const DEFAULT_ERROR_MESSAGE = 'К сожалению, произошла ошибка. Попробуйте позже еще раз.';

  export default {
    components: {
      Icon,
      Popup,
      Countdown,
    },
    mixins: [validationMixin],
    props: {
      pies: {
        type: Object,
        required: true,
      },
      randomUrl: {
        type: String,
        required: true,
      },
      sendUrl: {
        type: String,
        required: true,
      },
      shareParams: {
        type: Object,
        default() {
          return {};
        },
      },
      attempts: {
        type: Number,
        default: 2,
      },
      attemptsDone: {
        type: Number,
        default: 0,
      },
      discountId: {
        type: String,
        default: '',
      },
      discountTimestamp: {
        type: [String, Number],
        default: '',
      },
      discountSent: {
        type: Boolean,
        default: false,
      },
      isSuper: {
        type: Boolean,
        default: false,
      },
      rollSpeed: {
        type: Number,
        default: 180,
      },
      fakeRolls: {
        type: Number,
        default: 1,
      },
    },
    data() {
      return {
        rotateAngle: 0,
        rotateTarget: null,

        mutableAttemptsDone: null,
        mutableDiscount: null,
        paper: null,
        mutablePies: [],

        isDiscountSent: false,
        discountValidUntil: null,

        email: null,
        formError: null,
        error: null,

        isRolling: false,
        isWaitingRoll: false,
        isWaitShare: false,
        isShowPopupForm: false,
        isShowPopupAdvice: false,
        isSending: false,
      };
    },
    computed: {
      attemptsDecl() {
        return declOfNums(this.mutableAttemptsDone, ['попытка', 'попытки', 'попыток']);
      },
      usedDecl() {
        return declOfNums(this.mutableAttemptsDone, ['Использована', 'Использовано', 'Использовано']);
      },
      hasAttempts() {
        return this.mutableAttemptsDone < this.attempts;
      },
      showPlayAgain() {
        if (!this.hasAttempts) return false;

        const attemptsLeft = this.attempts - this.mutableAttemptsDone;

        if (attemptsLeft === 1 && (this.isWaitingRoll || this.isRolling)) {
          return false;
        }

        return true;
      },
    },
    validations: {
      email: {
        required,
        email,
      },
      fields: ['email'],
    },
    created() {
      this.mutableAttemptsDone = this.attemptsDone;
    },
    mounted() {
      this.draw(this.formatPies(this.pies));
      this.isWaitingRoll = this.hasAttempts;

      if (this.discountId) {
        const winner = this.mutablePies.find((pie) => pie.id === this.discountId);

        if (!winner) return;

        $(winner.node).addClass('is-winner');
        this.mutableDiscount = winner.value;
        this.isWaitingRoll = false;
        this.rotateAngle = 360 - winner.angle;
        this.discountValidUntil = this.discountTimestamp || Date.now();

        // TODO: discount received param
      }

      this.isDiscountSent = this.discountSent;
    },
    methods: {
      formatPies(pies) {
        const result = [];
        Object.keys(pies).forEach((pie) => {
          result.push({
            id: pie,
            value: pies[pie].value,
            fill: pies[pie].fill,
            labels: [
              {
                text: pies[pie].value,
                position: 3.7,
                attrs: {
                  'font-size': '8',
                  'font-weight': 'bold',
                  'font-family': 'Tondo, sans-serif',
                  fill: '#fff',
                },
              },
              {
                text: '%',
                position: 0.8,
                attrs: {
                  'font-size': '5',
                  'font-weight': 'bold',
                  'font-family': 'Tondo, sans-serif',
                  fill: '#fff',
                },
              },
            ],
          });
        });
        return result;
      },
      draw(pies) {
        this.paper = Raphael(this.$refs.drum, 380, 380);
        this.paper.setViewBox(0, 0, 100, 100, true);

        const R = 50; // don't change this
        const PIES_START = 90; // don't change this

        // this.winnerPieClass = 'roulette-winner'; ////

        function createPieData(x, y, r, a1, a2) {
          // https://jbkflex.wordpress.com/2011/07/28/creating-a-svg-pie-chart-html5/

          const flag = (a2 - a1) > 180;
          const pieCenter = (a1 + a2) / 2;
          const labelRotate = PIES_START + (a2 - ((a2 - a1) / 2));

          // Point on pie arc center
          const center = ((pieCenter % 360) * Math.PI) / 180;
          const arcCenter = [x + (r * Math.cos(center)), y + (r * Math.sin(center))];

          a1 = ((a1 % 360) * Math.PI) / 180;
          a2 = ((a2 % 360) * Math.PI) / 180;

          return {
            pieCenter,
            labelRotate,
            arcCenter,
            path: [
              ['M', x, y],
              ['l', r * Math.cos(a1), r * Math.sin(a1)],
              ['A', r, r, 0, +flag, 1, x + (r * Math.cos(a2)), y + (r * Math.sin(a2))], ['z']],
          };
        }

        function getPieLabelCoords(arcCenter, delta) {
          const x = (R + (arcCenter[0] * delta)) / (1 + delta);
          const y = (R + (arcCenter[1] * delta)) / (1 + delta);
          return [x, y];
        }

        let totalPiesSize = 0;
        let pieStart = 0; // Start of next pie

        // Collect common pies size for convert it to angles
        pies.forEach((pie) => {
          totalPiesSize += pie.size || 1;
        });

        // Process pies
        pies.forEach((pie) => {
          const size = 360 / (totalPiesSize * (pie.size || 1));
          const pieData = createPieData(R, R, R, pieStart, pieStart + size);

          // Draw pie
          const piePath = this.paper.path();
          piePath.attr({
            path: pieData.path,
            fill: pie.fill,
            stroke: 'none',
            class: 'roulette__pie',
          });

          // Draw labels
          if (pie.labels && Array.isArray(pie.labels)) {
            pie.labels.forEach((label) => {
              const coords = getPieLabelCoords(pieData.arcCenter, label.position || 3.8);
              const text = this.paper.text(coords[0], coords[1], label.text || '');
              text.attr(label.attrs || {});
              text.rotate(pieData.labelRotate);
            });
          }

          this.mutablePies.push({
            id: pie.id,
            value: pie.value,
            angle: (pieData.pieCenter + PIES_START) % 360,
            node: piePath.node,
          });

          pieStart += size;
        });
      },
      roll(target, onComplete) {
        const self = this;
        const start = this.rotateAngle;

        const end = target !== null
          ? start + (360 - (start % 360)) + (360 - target) + (360 * this.fakeRolls)
          : start + 360;

        let time = ((end - start) / this.rollSpeed);

        if (target) {
          time *= 1.65; // Speed fix for easing
        }

        const rotate = { rotate: start };

        function onUpdate() {
          self.rotateAngle = rotate.rotate;
        }

        TweenLite.to(rotate, time, {
          rotate: end,
          ease: target ? Quad.easeOut : Linear.easeNone,
          onUpdate,
          onComplete,
        });
      },
      play() {
        if (!this.hasAttempts) return;

        this.$emit('play', { attempt: this.mutableAttemptsDone + 1 });

        this.error = null;
        this.rotateTarget = null;
        this.mutableDiscount = null;

        this.isRolling = true;
        this.isWaitingRoll = false;

        this.rotateAngle %= 360; // Clear laps

        Vue.nextTick(() => {
          this.isRolling = true;

          let attemptsDone = this.mutableAttemptsDone;
          let discount;
          let error;
          let winner;

          $.get(this.randomUrl)
            .done((response) => {
              if (response.error || !response.discount_id) {
                this.rotateTarget = 0;
                error = response.error || DEFAULT_ERROR_MESSAGE;
                return;
              }

              winner = this.mutablePies.find((pie) => pie.id === response.discount_id);

              if (!winner) {
                this.rotateTarget = 0;
                error = DEFAULT_ERROR_MESSAGE;
                return;
              }

              this.rotateTarget = winner.angle;
              discount = winner.value;
              attemptsDone += 1;
            })
            .fail(() => {
              this.rotateTarget = 0;
              error = DEFAULT_ERROR_MESSAGE;
            });

          const onComplete = () => {
            if (this.rotateTarget === null) {
              return this.roll(null, onComplete);
            }

            return this.roll(this.rotateTarget, () => {
              this.isRolling = false;
              this.error = error;

              if (winner) {
                this.mutableAttemptsDone = attemptsDone;
                this.mutableDiscount = discount;
                $(winner.node).addClass('is-winner');
                this.discountValidUntil = Date.now() + (1000 * 60 * 60 * 24 * 7);

                // Propose super game
                if (!this.isSuper && !this.hasAttempts) {
                  this.isShowPopupAdvice = true;
                }
              }
            });
          };

          this.roll(this.rotateTarget, onComplete);
        });
      },
      unlockPlay() {
        if (this.hasAttempts) {
          this.mutablePies.forEach((pie) => {
            $(pie.node).removeClass('is-winner');
          });
          this.mutableDiscount = null;
          this.isWaitingRoll = true;
        }
      },
      getDiscount() {
        this.isShowPopupForm = true;
        this.$emit('get-discount');
      },
      goToShare() {
        this.mutableDiscount = null;
        this.isWaitShare = true;
        this.mutablePies.forEach((pie) => {
          $(pie.node).removeClass('is-winner');
        });
        this.$emit('init-super');
      },
      share(target) {
        this.$emit('share', { target });

        const data = {};
        const params = this.shareParams[target];

        if (params) {
          if (params.url) {
            data.url = params.url;
          }

          if (params.text) {
            data.text = params.text;
          }
        }

        Unlockshare[target](() => {
          this.$emit('share-success', { target });
        }, data);
      },
      submitForm() {
        if (this.isSending) return;
        if (!this.$v.fields.$dirty) this.$v.$touch();
        if (this.$v.fields.$invalid) return;

        this.isSending = true;

        $.post(this.sendUrl, { e: this.email })
          .done((response) => {
            if (!response || response.result !== 'ok') {
              this.formError = response.message || DEFAULT_ERROR_MESSAGE;
            } else {
              this.isDiscountSent = true;
            }
          })
          .fail(() => {
            this.formError = DEFAULT_ERROR_MESSAGE;
          })
          .always(() => {
            this.isSending = false;
          });
      },
    },
  };
</script>

<template>
  <div
    :class="{ 'has-winner': mutableDiscount, 'is-done': isWaitShare }"
    class="roulette"
  >
    <div class="roulette__main">
      <div class="roulette__drum">
        <div
          ref="drum"
          :style="{ transform: 'rotate(' + rotateAngle + 'deg)'}"
          class="roulette__drum-el"
        />
        <button
          :disabled="isRolling || !isWaitingRoll"
          type="button"
          class="roulette__handle"
          @click="play"
        >
          <span
            v-if="!isRolling && isWaitingRoll"
            class="roulette__handle-advice"
          />
        </button>
      </div>
    </div>
    <div
      v-if="error"
      class="roulette__error"
    >
      {{ error }}
    </div>
    <!-- Controls -->
    <template v-if="!isWaitShare && !isDiscountSent">
      <div class="roulette__status">
        <template v-if="mutableAttemptsDone < attempts">
          {{ usedDecl }} <span class="text-color-sub">{{ mutableAttemptsDone }}</span> {{ attemptsDecl }} из <span class="text-color-brand">{{ attempts }}</span><!-- eslint-disable-line max-len -->
        </template>
        <template v-else>
          Все попытки использованы!
        </template>
      </div>
      <div class="row">
        <div class="grid grid_center">
          <div
            v-if="!isSuper && !hasAttempts"
            class="col col_4"
          >
            <button
              type="button"
              class="btn btn_brand btn_fluid"
              @click="goToShare"
            >
              <span>Играть в супер-рулетку</span>
            </button>
          </div>
          <div
            v-if="showPlayAgain"
            class="col col_4"
          >
            <button
              :disabled="(isRolling || isWaitingRoll) || !hasAttempts"
              type="button"
              class="btn btn_fluid"
              @click="unlockPlay"
            >
              <icon
                class="btn__icon"
                src="repeat"
              />
              &nbsp;
              <span>Попробовать ещё</span>
            </button>
          </div>
          <div class="col col_4">
            <button
              :disabled="!mutableDiscount || isRolling"
              type="button"
              class="btn btn_fluid"
              @click.prevent="getDiscount"
            >
              <span>Получить скидку <span v-if="mutableDiscount">{{ mutableDiscount }}%</span></span><!-- eslint-disable-line max-len -->
              &nbsp;
              <icon
                class="btn__icon"
                src="angle-right"
              />
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-if=" isDiscountSent">
      <div class="text-center">
        <div class="row wysiwyg">
          <h4>Мы отправили код с данными по <span class="text-color-brand">скидке</span> на вашу электронную почту.</h4><!-- eslint-disable-line max-len -->
        </div>
        <div class="text-big">
          До окончания действия скидки осталось
        </div>
        <countdown
          v-if="discountValidUntil"
          :timestamp="discountValidUntil"
        />
      </div>
    </template>
    <!-- Sharing -->
    <template v-if="isWaitShare">
      <div class="row text-big text-center">
        Чтобы сыграть в Супер-рулетку, расскажи подругам о том,<br>
        что на Лаки Чайлд можно выиграть скидку до 35%, сыграв с ними в игру
      </div>
      <div class="row">
        <div class="btns btns_stretch">
          <div class="btns__item">
            <button
              type="button"
              class="btn btn_vk btn_small-text btn_fluid"
              @click="share('vk')"
            >
              <icon
                class="btn__icon"
                src="social-vk"
              />
              &nbsp;
              <span>Вконтакте</span>
            </button>
          </div>
          <div class="btns__item">
            <button
              type="button"
              class="btn btn_fb btn_small-text btn_fluid"
              @click="share('fb')"
            >
              <icon
                class="btn__icon"
                src="social-fb"
              />
              &nbsp;
              <span>Facebook</span>
            </button>
          </div>
          <div class="btns__item">
            <button
              type="button"
              class="btn btn_ok btn_small-text btn_fluid"
              @click="share('ok')"
            >
              <icon
                class="btn__icon"
                src="social-ok"
              />
              &nbsp;
              <span>Одноклассники</span>
            </button>
          </div>
          <div class="btns__item">
            <button
              type="button"
              class="btn btn_tw btn_small-text btn_fluid"
              @click="share('tw')"
            >
              <icon
                class="btn__icon"
                src="social-tw"
              />
              &nbsp;
              <span>Twitter</span>
            </button>
          </div>
        </div>
      </div>
    </template>
    <!-- Popups -->
    <popup
      :show="isShowPopupForm"
      @close="isShowPopupForm = false"
    >
      <template v-if="!isDiscountSent">
        <div class="content-section wysiwyg text-center">
          <h2><span class="text-color-brand">Lucky </span><span class="text-color-sub">Child</span> — наши дети рождены, <br>чтобы быть успешными!</h2><!-- eslint-disable-line max-len -->
        </div>
        <div class="content-section">
          <form
            novalidate
            @submit.prevent="submitForm"
          >
            <div class="row grid grid_center">
              <div class="col col_10">
                <div class="form-row">
                  <input
                    v-model="email"
                    :class="{ 'is-invalid': $v.fields.$dirty && $v.email.$invalid }"
                    :disabled="isSending"
                    type="email"
                    class="input-text"
                    placeholder="Адрес электронной почты"
                    required
                  >
                  <ul
                    v-if="formError"
                    class="form-errors"
                  >
                    <li class="form-errors__item">
                      {{ formError }}
                    </li>
                  </ul>
                </div>
                <div class="form-row">
                  <button
                    type="submit"
                    class="btn btn_fluid"
                  >
                    <span>
                      Получить скидку <span v-if="mutableDiscount">{{ mutableDiscount }}%</span>
                    </span>
                    <icon
                      class="btn__icon btn__icon_right"
                      src="angle-right"
                    />
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </template>
      <template v-else>
        <div class="content-section wysiwyg text-center">
          <h2>
            Спасибо! <br>
            Мы отправили код с данными по <span class="text-color-brand">скидке</span> на вашу электронную почту.<!-- eslint-disable-line max-len -->
          </h2>
        </div>
      </template>
      <div class="content-section text-center">
        <div class="text-big">
          До окончания действия скидки осталось
        </div>
        <countdown
          v-if="discountValidUntil"
          :timestamp="discountValidUntil"
        />
      </div>
    </popup>
    <popup
      :show="isShowPopupAdvice"
      size="small"
      @close="isShowPopupAdvice = false"
    >
      <div class="content-section wysiwyg text-center">
        <p>К сожалению, твои попытки закончились.</p>
        <p>Ты можешь получить выигранную скидку <span class="text-color-sub">{{ mutableDiscount }}%</span> или сыграть в Супер-рулетку и получить скидку <span class="text-color-brand">от 25 до 35%</span> гарантированно!</p><!-- eslint-disable-line max-len -->
      </div>
      <div class="content-section">
        <div class="group">
          <div class="group__col group__col_6">
            <button
              type="button"
              class="btn btn_fluid btn_brand"
              @click="isShowPopupAdvice = false; goToShare()"
            >
              <span>Играть в супер-рулетку</span>
            </button>
          </div>
          <div class="group__col group__col_6">
            <button
              type="button"
              class="btn btn_fluid btn_sub"
              @click="isShowPopupForm = true; isShowPopupAdvice = false;"
            >
              Получить скидку {{ mutableDiscount }}%
            </button>
          </div>
        </div>
      </div>
    </popup>
  </div>
</template>
